import React, { useState } from 'react';

interface CellChange {
  field: string;
  displayLabel: string;
  oldValue: string | null;
  newValue: string | null;
}

interface RowError {
  field: string;
  message: string;
}

interface ValidatedRow {
  rowIndex: number;
  enquiryId: number;
  studentName: string;
  status: 'changed' | 'unchanged' | 'error';
  errors: RowError[];
  changes: CellChange[];
}

interface Summary {
  totalRows: number;
  changedRows: number;
  unchangedRows: number;
  errorRows: number;
}

interface BulkUpdatePreviewTableProps {
  summary: Summary;
  rows: ValidatedRow[];
}

type Filter = 'all' | 'changed' | 'error';

const styles = {
  container: { marginTop: 8 } as React.CSSProperties,
  summaryBar: {
    display: 'flex', gap: 16, padding: '10px 14px', marginBottom: 12,
    background: 'var(--surface-page)', borderRadius: 6, alignItems: 'center', flexWrap: 'wrap' as const,
  },
  summaryItem: { fontSize: 13, fontWeight: 500 } as React.CSSProperties,
  changedBadge: {
    display: 'inline-block', padding: '2px 8px', borderRadius: 4,
    background: 'var(--status-success-bg)', color: 'var(--status-success-text)', fontSize: 12, fontWeight: 600,
  } as React.CSSProperties,
  errorBadge: {
    display: 'inline-block', padding: '2px 8px', borderRadius: 4,
    background: 'var(--status-danger-bg)', color: 'var(--status-danger-text)', fontSize: 12, fontWeight: 600,
  } as React.CSSProperties,
  unchangedBadge: {
    display: 'inline-block', padding: '2px 8px', borderRadius: 4,
    background: '#e2e3e5', color: '#383d41', fontSize: 12, fontWeight: 600,
  } as React.CSSProperties,
  filterBar: {
    display: 'flex', gap: 6, marginBottom: 10,
  } as React.CSSProperties,
  filterBtn: (active: boolean) => ({
    padding: '4px 12px', border: '1px solid var(--border-strong)', borderRadius: 4,
    background: active ? '#4472C4' : '#fff', color: active ? '#fff' : '#333',
    cursor: 'pointer', fontSize: 12, fontWeight: 500,
  }) as React.CSSProperties,
  tableWrap: {
    maxHeight: 400, overflowY: 'auto' as const, overflowX: 'auto' as const, border: '1px solid var(--border-default)', borderRadius: 4,
  } as React.CSSProperties,
  table: {
    width: '100%', borderCollapse: 'collapse' as const, fontSize: 13,
  } as React.CSSProperties,
  th: {
    padding: '8px 10px', background: '#4472C4', color: 'var(--text-on-brand)', fontWeight: 600,
    textAlign: 'left' as const, position: 'sticky' as const, top: 0, zIndex: 1, whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,
  td: {
    padding: '6px 10px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'top' as const,
  } as React.CSSProperties,
  changeCellOld: {
    color: 'var(--text-faint)', textDecoration: 'line-through' as const, fontSize: 12,
  } as React.CSSProperties,
  changeCellNew: {
    color: 'var(--status-success-text)', fontWeight: 600, fontSize: 12,
  } as React.CSSProperties,
  changeCellArrow: {
    color: 'var(--text-muted)', margin: '0 4px', fontSize: 11,
  } as React.CSSProperties,
  errorCell: {
    color: 'var(--status-danger-text)', fontSize: 12,
  } as React.CSSProperties,
  noData: {
    padding: 20, textAlign: 'center' as const, color: 'var(--text-muted)',
  } as React.CSSProperties,
};

const BulkUpdatePreviewTable: React.FC<BulkUpdatePreviewTableProps> = ({ summary, rows }) => {
  const [filter, setFilter] = useState<Filter>('all');

  const filteredRows = rows.filter((r) => {
    if (filter === 'all') return r.status !== 'unchanged';
    return r.status === filter;
  });

  // Collect all unique change field labels across visible rows
  const changeLabels = new Map<string, string>();
  for (const row of filteredRows) {
    for (const c of row.changes) {
      if (!changeLabels.has(c.field)) changeLabels.set(c.field, c.displayLabel);
    }
  }
  const changeFields = Array.from(changeLabels.entries()); // [field, displayLabel]

  return (
    <div style={styles.container}>
      {/* Summary bar */}
      <div style={styles.summaryBar}>
        <span style={styles.summaryItem}>Total: {summary.totalRows}</span>
        <span style={styles.changedBadge}>{summary.changedRows} changed</span>
        <span style={styles.errorBadge}>{summary.errorRows} errors</span>
        <span style={styles.unchangedBadge}>{summary.unchangedRows} unchanged</span>
      </div>

      {/* Filter tabs */}
      <div style={styles.filterBar}>
        <button style={styles.filterBtn(filter === 'all')} onClick={() => setFilter('all')}>
          Changed + Errors ({summary.changedRows + summary.errorRows})
        </button>
        <button style={styles.filterBtn(filter === 'changed')} onClick={() => setFilter('changed')}>
          Changed ({summary.changedRows})
        </button>
        <button style={styles.filterBtn(filter === 'error')} onClick={() => setFilter('error')}>
          Errors ({summary.errorRows})
        </button>
      </div>

      {/* Table */}
      {filteredRows.length === 0 ? (
        <div style={styles.noData}>
          {summary.changedRows === 0 && summary.errorRows === 0
            ? 'No changes detected. The uploaded data matches the current database values.'
            : 'No rows match the selected filter.'}
        </div>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Enquiry ID</th>
                <th style={styles.th}>Student Name</th>
                <th style={styles.th}>Status</th>
                {changeFields.map(([field, label]) => (
                  <th key={field} style={styles.th}>{label}</th>
                ))}
                {filter !== 'changed' && <th style={styles.th}>Errors</th>}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.rowIndex} style={{ background: row.status === 'error' ? '#fff5f5' : undefined }}>
                  <td style={styles.td}>{row.rowIndex + 1}</td>
                  <td style={styles.td}>{row.enquiryId}</td>
                  <td style={styles.td}>{row.studentName}</td>
                  <td style={styles.td}>
                    {row.status === 'changed' && <span style={styles.changedBadge}>Changed</span>}
                    {row.status === 'error' && <span style={styles.errorBadge}>Error</span>}
                  </td>
                  {changeFields.map(([field]) => {
                    const change = row.changes.find((c) => c.field === field);
                    return (
                      <td key={field} style={{ ...styles.td, background: change ? '#edf7ed' : undefined }}>
                        {change ? (
                          <span>
                            <span style={styles.changeCellOld}>{change.oldValue || '(empty)'}</span>
                            <span style={styles.changeCellArrow}>&rarr;</span>
                            <span style={styles.changeCellNew}>{change.newValue || '(empty)'}</span>
                          </span>
                        ) : null}
                      </td>
                    );
                  })}
                  {filter !== 'changed' && (
                    <td style={styles.td}>
                      {row.errors.map((err, ei) => (
                        <div key={ei} style={styles.errorCell}>
                          <strong>{err.field}:</strong> {err.message}
                        </div>
                      ))}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BulkUpdatePreviewTable;
